// Night-sky canvas: layered drifting stars, fine diffraction glints, silk-thin comets, and a comet strike that precedes star-link navigation.
const STAR_TINTS = ['255,255,255', '232,238,255', '255,246,236'];

class StarfieldElement extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('[data-starfield-canvas]');
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.starCount = Number(this.dataset.starCount) || 420;
    this.cometsEnabled = this.dataset.comets !== 'false';
    this.comets = [];
    this.flashes = [];
    // First comet arrives shortly after load so visitors see one right away.
    this.nextComet = performance.now() + 1200;
    this.lastTime = 0;
    this.pointer = { x: 0, y: 0, tx: 0, ty: 0 };
    this.running = false;
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this);

    this.intersection = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.toggleLoop();
    });
    this.intersection.observe(this);
    document.addEventListener('visibilitychange', () => this.toggleLoop(), { signal });

    // Gentle parallax: layers lean a few pixels toward the cursor.
    this.addEventListener('pointermove', (event) => {
      const rect = this.getBoundingClientRect();
      this.pointer.tx = (event.clientX - rect.left) / rect.width - 0.5;
      this.pointer.ty = (event.clientY - rect.top) / rect.height - 0.5;
    }, { signal });

    this.addEventListener('click', (event) => this.onStarClick(event), { signal });
    // Returning via the back button restores a cached page; clear the strike state so stars work again.
    window.addEventListener('pageshow', () => {
      for (const link of this.querySelectorAll('[data-star-link]')) {
        delete link.dataset.launching;
        link.removeAttribute('data-hit');
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    this.intersection?.disconnect();
    this.running = false;
    cancelAnimationFrame(this.frame);
  }

  resize() {
    const rect = this.getBoundingClientRect();
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.seedStars();
    this.nebula = null; // Haze removed: the sky stays true black so stars and the chrome wordmark read crisp.
    if (this.reducedMotion || !this.running) this.draw(performance.now());
  }

  seedStars() {
    // Scale density with area so phones are not packed solid; keep the merchant's count as the desktop target.
    const areaRatio = Math.min(1, (this.width * this.height) / (1440 * 900));
    const count = Math.max(90, Math.round(this.starCount * (0.45 + 0.55 * areaRatio)));
    this.stars = Array.from({ length: count }, () => {
      const layer = Math.random() < 0.6 ? 0 : Math.random() < 0.75 ? 1 : 2;
      const glint = layer === 2 && Math.random() < 0.18;
      return {
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        layer,
        r: glint ? 0.9 + Math.random() * 0.7 : [0.35, 0.65, 1.05][layer] * (0.6 + Math.random() * 0.6),
        base: [0.35, 0.55, 0.85][layer] * (0.6 + Math.random() * 0.4),
        speed: 0.3 + Math.random() * 1.4,
        phase: Math.random() * Math.PI * 2,
        tint: STAR_TINTS[Math.floor(Math.random() * STAR_TINTS.length)],
        glint,
        spike: 6 + Math.random() * 10,
      };
    });
  }

  paintNebula() {
    // Faint silver haze, painted once offscreen so frames stay cheap.
    const haze = document.createElement('canvas');
    haze.width = this.canvas.width;
    haze.height = this.canvas.height;
    const h = haze.getContext('2d');
    h.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const clouds = [
      [0.72, 0.28, 0.55, 0.07],
      [0.2, 0.7, 0.5, 0.05],
      [0.5, 0.55, 0.9, 0.035],
    ];
    for (const [cx, cy, radius, alpha] of clouds) {
      const x = cx * this.width;
      const y = cy * this.height;
      const r = radius * Math.max(this.width, this.height);
      const g = h.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(220,226,240,${alpha})`);
      g.addColorStop(1, 'rgba(220,226,240,0)');
      h.fillStyle = g;
      h.fillRect(0, 0, this.width, this.height);
    }
    this.nebula = haze;
  }

  toggleLoop() {
    const shouldRun = !this.reducedMotion && this.visible && document.visibilityState === 'visible';
    if (shouldRun && !this.running) {
      this.running = true;
      this.lastTime = performance.now();
      const tick = (time) => {
        if (!this.running) return;
        this.draw(time);
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    } else if (!shouldRun && this.running) {
      this.running = false;
      cancelAnimationFrame(this.frame);
    }
  }

  draw(time) {
    const { ctx } = this;
    const t = time / 1000;
    const dt = Math.min(0.05, Math.max(0, (time - this.lastTime) / 1000));
    this.lastTime = time;
    ctx.clearRect(0, 0, this.width, this.height);
    if (this.nebula) ctx.drawImage(this.nebula, 0, 0, this.width, this.height);

    this.pointer.x += (this.pointer.tx - this.pointer.x) * 0.04;
    this.pointer.y += (this.pointer.ty - this.pointer.y) * 0.04;

    for (const star of this.stars || []) {
      if (!this.reducedMotion) {
        star.x += [1.2, 2.6, 4.5][star.layer] * dt;
        if (star.x > this.width + 20) star.x = -20;
      }
      const depth = [4, 10, 18][star.layer];
      const x = star.x + this.pointer.x * depth;
      const y = star.y + this.pointer.y * depth;
      const twinkle = this.reducedMotion ? 1 : 0.6 + 0.4 * Math.sin(t * star.speed + star.phase);
      const alpha = star.base * twinkle;
      if (star.glint) {
        this.drawGlint(x, y, star, alpha, twinkle);
      } else {
        ctx.globalAlpha = alpha;
        ctx.fillStyle = `rgb(${star.tint})`;
        ctx.beginPath();
        ctx.arc(x, y, star.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;

    if (this.reducedMotion) return;
    if (this.cometsEnabled && time > this.nextComet) {
      this.spawnAmbientComet(time);
      if (Math.random() < 0.3) this.spawnAmbientComet(time + 350);
      this.nextComet = time + 1800 + Math.random() * 2600;
    }
    this.drawComets(time);
    this.drawFlashes(time);
  }

  drawGlint(x, y, star, alpha, twinkle) {
    // Diffraction glint: hairline spikes that fade to nothing, plus a tight core and halo.
    const { ctx } = this;
    const len = star.spike * (0.7 + 0.5 * twinkle);
    ctx.globalAlpha = 1;
    for (const [dx, dy, scale] of [[1, 0, 1], [0, 1, 1], [0.7, 0.7, 0.35], [0.7, -0.7, 0.35]]) {
      const l = len * scale;
      const g = ctx.createLinearGradient(x - dx * l, y - dy * l, x + dx * l, y + dy * l);
      g.addColorStop(0, `rgba(${star.tint},0)`);
      g.addColorStop(0.5, `rgba(${star.tint},${alpha})`);
      g.addColorStop(1, `rgba(${star.tint},0)`);
      ctx.strokeStyle = g;
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(x - dx * l, y - dy * l);
      ctx.lineTo(x + dx * l, y + dy * l);
      ctx.stroke();
    }
    const halo = ctx.createRadialGradient(x, y, 0, x, y, star.r * 4);
    halo.addColorStop(0, `rgba(${star.tint},${alpha})`);
    halo.addColorStop(0.35, `rgba(${star.tint},${alpha * 0.25})`);
    halo.addColorStop(1, `rgba(${star.tint},0)`);
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(x, y, star.r * 4, 0, Math.PI * 2);
    ctx.fill();
  }

  spawnAmbientComet(time) {
    const fromTop = Math.random() < 0.65;
    const startX = fromTop ? Math.random() * this.width * 0.8 : -60;
    const startY = fromTop ? -60 : Math.random() * this.height * 0.45;
    const angle = (18 + Math.random() * 22) * (Math.PI / 180);
    const distance = Math.hypot(this.width, this.height) * 0.9;
    this.comets.push({
      x0: startX,
      y0: startY,
      x1: startX + Math.cos(angle) * distance,
      y1: startY + Math.sin(angle) * distance,
      start: time,
      duration: 1700 + Math.random() * 1300,
      tail: 280 + Math.random() * 160,
      width: 1.4,
    });
  }

  drawComets(time) {
    const { ctx } = this;
    this.comets = this.comets.filter((comet) => {
      const p = Math.min(1, (time - comet.start) / comet.duration);
      const eased = comet.onHit ? 1 - (1 - p) ** 3 : p;
      // Ambient comets fade in and out instead of popping at the edges.
      const fade = comet.onHit ? 1 : Math.min(1, p * 6, (1 - p) * 4);
      const x = comet.x0 + (comet.x1 - comet.x0) * eased;
      const y = comet.y0 + (comet.y1 - comet.y0) * eased;
      const dx = comet.x1 - comet.x0;
      const dy = comet.y1 - comet.y0;
      const len = Math.hypot(dx, dy) || 1;
      const tail = comet.tail * (comet.onHit ? Math.min(1, p * 3) : 1);
      const tx = x - (dx / len) * tail;
      const ty = y - (dy / len) * tail;

      const trail = ctx.createLinearGradient(tx, ty, x, y);
      trail.addColorStop(0, 'rgba(255,255,255,0)');
      trail.addColorStop(0.65, `rgba(235,240,255,${0.4 * fade})`);
      trail.addColorStop(1, `rgba(255,255,255,${0.95 * fade})`);
      ctx.strokeStyle = trail;
      ctx.lineWidth = comet.width;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(x, y);
      ctx.stroke();

      const headRadius = comet.onHit ? 8 : 5;
      const head = ctx.createRadialGradient(x, y, 0, x, y, headRadius);
      head.addColorStop(0, `rgba(255,255,255,${fade})`);
      head.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = head;
      ctx.beginPath();
      ctx.arc(x, y, headRadius, 0, Math.PI * 2);
      ctx.fill();

      if (p >= 1) {
        comet.onHit?.();
        return false;
      }
      return true;
    });
  }

  drawFlashes(time) {
    // Impact: a burst of fine light rays shooting outward and a brief white bloom.
    const { ctx } = this;
    this.flashes = this.flashes.filter((flash) => {
      const p = Math.min(1, (time - flash.start) / 600);
      const ease = 1 - (1 - p) ** 3;
      ctx.globalAlpha = 1 - p;
      ctx.lineWidth = 0.8;
      ctx.lineCap = 'round';
      for (let i = 0; i < 12; i += 1) {
        const angle = (i / 12) * Math.PI * 2 + Math.PI / 12;
        const long = i % 3 === 0 ? 1 : 0.55;
        const inner = 12 + ease * 30 * long;
        const outer = 18 + ease * 110 * long;
        const ray = ctx.createLinearGradient(
          flash.x + Math.cos(angle) * inner, flash.y + Math.sin(angle) * inner,
          flash.x + Math.cos(angle) * outer, flash.y + Math.sin(angle) * outer,
        );
        ray.addColorStop(0, 'rgba(255,255,255,0.95)');
        ray.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = ray;
        ctx.beginPath();
        ctx.moveTo(flash.x + Math.cos(angle) * inner, flash.y + Math.sin(angle) * inner);
        ctx.lineTo(flash.x + Math.cos(angle) * outer, flash.y + Math.sin(angle) * outer);
        ctx.stroke();
      }
      const bloom = ctx.createRadialGradient(flash.x, flash.y, 0, flash.x, flash.y, 40 + ease * 30);
      bloom.addColorStop(0, 'rgba(255,255,255,0.8)');
      bloom.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = bloom;
      ctx.beginPath();
      ctx.arc(flash.x, flash.y, 40 + ease * 30, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      return p < 1;
    });
  }

  onStarClick(event) {
    const link = event.target.closest('[data-star-link]');
    if (!link || !this.contains(link)) return;
    // A star whose link points at a dialog on this page (e.g. the signup form) opens it instead of navigating.
    const dialogTarget = link.hash ? document.getElementById(decodeURIComponent(link.hash.slice(1))) : null;
    const opensDialog = dialogTarget instanceof HTMLDialogElement;
    if (opensDialog && this.reducedMotion) {
      event.preventDefault();
      if (!dialogTarget.open) dialogTarget.showModal();
      return;
    }
    // Let modified clicks (new tab) and reduced-motion visitors go straight through.
    if (this.reducedMotion || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    if (link.dataset.launching) return;
    link.dataset.launching = 'true';

    const host = this.getBoundingClientRect();
    const glyph = (link.querySelector('svg')?.parentElement || link).getBoundingClientRect();
    const targetX = glyph.left + glyph.width / 2 - host.left;
    const targetY = glyph.top + glyph.height / 2 - host.top;
    const fromStart = targetX > this.width / 2;
    const startX = fromStart ? -80 : this.width + 80;
    const startY = Math.max(-80, targetY - this.height * 0.55);

    const navigate = () => {
      link.toggleAttribute('data-hit', true);
      this.flashes.push({ x: targetX, y: targetY, start: performance.now() });
      setTimeout(() => {
        if (opensDialog) {
          delete link.dataset.launching;
          link.removeAttribute('data-hit');
          if (!dialogTarget.open) dialogTarget.showModal();
          return;
        }
        window.location.assign(link.href);
      }, 380);
    };

    if (!this.running) {
      navigate();
      return;
    }
    this.comets.push({
      x0: startX,
      y0: startY,
      x1: targetX,
      y1: targetY,
      start: performance.now(),
      duration: 820,
      tail: 260,
      width: 1.6,
      onHit: navigate,
    });
  }
}

if (!customElements.get('s-starfield')) customElements.define('s-starfield', StarfieldElement);
